import { supabase } from '../lib/supabase';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const SESSION_KEY = 'gymplix_session_id';
let sessionLockPromise = null;

async function runWithSessionLock(task) {
  if (sessionLockPromise) return sessionLockPromise;
  sessionLockPromise = (async () => {
    try {
      return await task();
    } finally {
      sessionLockPromise = null;
    }
  })();
  return sessionLockPromise;
}

export function getSessionId() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_KEY);
}

export function setSessionId(sessionId) {
  if (typeof window === 'undefined') return;
  if (sessionId) localStorage.setItem(SESSION_KEY, sessionId);
}

export function clearSessionId() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
}

export function getSessionHeaders() {
  const sessionId = getSessionId();
  return sessionId ? { 'x-session-id': sessionId } : {};
}

async function getAuthHeader() {
  const { data: { session } } = await supabase?.auth?.getSession();
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getAuthSessionHeaders() {
  const ensured = await ensureSession();
  const sessionId = ensured?.sessionId || getSessionId();
  const authHeader = await getAuthHeader();
  return {
    ...(sessionId ? { 'x-session-id': sessionId } : {}),
    ...authHeader
  };
}
export async function loginFailed(email) {
  try {
    const authHeader = await getAuthHeader();
    await supabase?.functions?.invoke('login-failed', {
      body: { email }
      ,headers: authHeader
    });
  } catch (_) {
    // best-effort
  }
}

async function startSessionInternal() {
  const existingSessionId = getSessionId();
  const deviceFingerprint = getDeviceFingerprint();
  const authHeader = await getAuthHeader();

  const { data, error } = await supabase?.functions?.invoke('session-start', {
    body: {
      sessionId: existingSessionId || null,
      deviceFingerprint
    },
    headers: {
      ...(existingSessionId ? { 'x-session-id': existingSessionId } : {}),
      ...authHeader
    }
  });

  if (error || data?.error) {
    throw new Error(data?.error || error?.message || 'Session start failed');
  }

  if (data?.sessionId) {
    setSessionId(data.sessionId);
  }

  return data;
}

export async function startSession() {
  return runWithSessionLock(startSessionInternal);
}

export async function validateSession() {
  const sessionId = getSessionId();
  if (!sessionId) return { valid: false };

  const authHeader = await getAuthHeader();
  const { data, error } = await supabase?.functions?.invoke('session-validate', {
    body: { sessionId },
    headers: { 'x-session-id': sessionId, ...authHeader }
  });

  if (error || data?.error) return { valid: false };
  if (data?.sessionId && data?.sessionId !== sessionId) {
    setSessionId(data.sessionId);
  }
  return { valid: true };
}

export async function ensureSession() {
  return runWithSessionLock(async () => {
    const sessionId = getSessionId();
    if (!sessionId) {
      return startSessionInternal();
    }

    const validation = await validateSession();
    if (!validation?.valid) {
      return startSessionInternal();
    }

    return { sessionId };
  });
}

export async function endSession() {
  const sessionId = getSessionId();
  if (!sessionId) return;

  const authHeader = await getAuthHeader();
  await supabase?.functions?.invoke('session-end', {
    body: { sessionId },
    headers: { 'x-session-id': sessionId, ...authHeader }
  });

  clearSessionId();
}

export async function issueUploadToken(folder) {
  const ensured = await ensureSession();
  const sessionId = ensured?.sessionId || getSessionId();
  const authHeader = await getAuthHeader();
  const { data, error } = await supabase?.functions?.invoke('issue-upload-token', {
    body: { folder, sessionId },
    headers: {
      ...(sessionId ? { 'x-session-id': sessionId } : {}),
      ...authHeader
    }
  });

  if (error || data?.error) {
    throw new Error(data?.error || error?.message || 'Upload token failed');
  }

  if (data?.sessionId) {
    setSessionId(data.sessionId);
  }
  return data;
}
